import { useAuth } from '@/context/AuthContext';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiWizardCreateTrip, aiWizardSuggestDestinations, getFamilies } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  MapPin, Calendar, Users, Sparkles, ChevronRight, ChevronLeft,
  X, Plus, Check, Globe, Plane, Hotel, Car, Ticket, Loader2,
  Zap, BookOpen, Sun, Heart, Compass, Coffee, Utensils, ShoppingBag,
  TreePine, Moon, Waves, Camera, ArrowRight, Info
} from 'lucide-react';

// ─── Popular destinations ────────────────────────────────────────
const POPULAR = [
  { name: 'Paris', country: 'France', emoji: '🗼' },
  { name: 'Tokyo', country: 'Japon', emoji: '🗾' },
  { name: 'New York', country: 'États-Unis', emoji: '🗽' },
  { name: 'Barcelone', country: 'Espagne', emoji: '🏖️' },
  { name: 'Bali', country: 'Indonésie', emoji: '🌴' },
  { name: 'Rome', country: 'Italie', emoji: '🏛️' },
  { name: 'Lisbonne', country: 'Portugal', emoji: '🚃' },
  { name: 'Marrakech', country: 'Maroc', emoji: '🕌' },
];

const TRIP_STYLES = [
  { id: 'adventure', label: 'Aventure', Icon: Zap, color: 'from-orange-500 to-red-500' },
  { id: 'culture', label: 'Culture', Icon: BookOpen, color: 'from-blue-500 to-indigo-600' },
  { id: 'beach', label: 'Plage', Icon: Sun, color: 'from-yellow-400 to-orange-400' },
  { id: 'family', label: 'Famille', Icon: Heart, color: 'from-pink-500 to-rose-500' },
  { id: 'citybreak', label: 'City Break', Icon: Coffee, color: 'from-purple-500 to-violet-600' },
  { id: 'roadtrip', label: 'Road Trip', Icon: Compass, color: 'from-green-500 to-teal-500' },
];

const BOOKING_TYPES = [
  { id: 'flights', label: 'Vols', Icon: Plane },
  { id: 'hotel', label: 'Hébergement', Icon: Hotel },
  { id: 'car', label: 'Voiture', Icon: Car },
  { id: 'activities', label: 'Activités', Icon: Ticket },
];

const DURATIONS = [
  { id: 'weekend', label: 'Weekend', days: 2 },
  { id: 'short', label: '4-5 jours', days: 5 },
  { id: 'week', label: '1 semaine', days: 7 },
  { id: 'twoweeks', label: '2 semaines', days: 14 },
  { id: 'month', label: '1 mois', days: 30 },
  { id: 'custom', label: 'Personnalisé', days: null },
];

const AI_OPTIONS = [
  { id: 'full', label: 'Programme complet', sub: 'Itinéraire jour par jour pour tout le séjour', Icon: Sparkles, color: 'from-purple-600 to-pink-500' },
  { id: 'partial', label: 'Quelques jours', sub: 'Je prépare les 3 premiers jours pour vous lancer', Icon: Calendar, color: 'from-blue-600 to-purple-500' },
  { id: 'inspiration', label: 'Juste des idées', sub: 'Suggestions thématiques, vous faites le reste', Icon: Zap, color: 'from-orange-500 to-pink-500' },
  { id: 'none', label: 'Sans IA', sub: 'Je créerai mon planning moi-même', Icon: Globe, color: 'from-slate-600 to-slate-500' },
];

// ─── Day brief chips ─────────────────────────────────────────────
const DAY_CHIPS = [
  { id: 'visite', label: 'Visites & musées', Icon: Camera },
  { id: 'plage', label: 'Plage & mer', Icon: Waves },
  { id: 'gastro', label: 'Gastronomie', Icon: Utensils },
  { id: 'shopping', label: 'Shopping', Icon: ShoppingBag },
  { id: 'nature', label: 'Nature', Icon: TreePine },
  { id: 'repos', label: 'Journée calme', Icon: Sun },
  { id: 'nuit', label: 'Soirée animée', Icon: Moon },
  { id: 'transit', label: 'Transit / trajet', Icon: Plane },
];

const LOADING_MESSAGES = [
  "Je compose votre itinéraire...",
  "Je recherche les meilleures adresses...",
  "J'optimise vos journées...",
  "J'ajoute quelques pépites locales...",
  "Votre voyage prend forme...",
  "Dernières touches...",
];

const DEST_COLORS = [
  'from-purple-600 to-pink-500',
  'from-blue-600 to-cyan-500',
  'from-orange-500 to-yellow-500',
  'from-green-600 to-teal-500',
  'from-red-500 to-orange-500',
];

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? '55%' : '-55%', opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.32, ease: [0.32, 0, 0.67, 0] } },
  exit: (d) => ({ x: d > 0 ? '-55%' : '55%', opacity: 0, transition: { duration: 0.2 } }),
};

// ─── Compute dynamic step list ────────────────────────────────────
const computeSteps = (data) => {
  const base = [
    { id: 1, label: 'Destinations' },
    { id: 2, label: 'Dates' },
    { id: 3, label: 'Voyageurs' },
    { id: 4, label: 'Contexte' },
    { id: 5, label: 'Programme' },
  ];
  if (data.destinations.length > 1 && ['full', 'partial'].includes(data.aiGeneration)) {
    base.push({ id: 6, label: 'Répartition', optional: true });
  }
  if (['full', 'partial'].includes(data.aiGeneration) && data.startDate && data.endDate) {
    base.push({ id: 7, label: 'Vos journées', optional: true });
  }
  return base;
};

const getNextStep = (currentStep, data) => {
  const steps = computeSteps(data);
  const idx = steps.findIndex(s => s.id === currentStep);
  return idx < steps.length - 1 ? steps[idx + 1].id : null;
};

const getPrevStep = (currentStep, data) => {
  const steps = computeSteps(data);
  const idx = steps.findIndex(s => s.id === currentStep);
  return idx > 0 ? steps[idx - 1].id : null;
};

// ─── Trip day list generator ──────────────────────────────────────
const buildDayList = (startDate, endDate, aiGeneration) => {
  if (!startDate || !endDate) return [];
  const days = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const totalDays = Math.round((end - start) / 86400000) + 1;
  const max = aiGeneration === 'partial' ? Math.min(3, totalDays) : Math.min(14, totalDays);
  const DAY_NAMES = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MONTH_NAMES = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
  for (let i = 0; i < max; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push({
      date: d.toISOString().slice(0, 10),
      dayNum: i + 1,
      label: `${DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`,
      brief: '',
      chips: [],
    });
  }
  return days;
};

export default function TripCreate() {
  const { families, setFamilies } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [creating, setCreating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [destInput, setDestInput] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const suggestTimer = useRef(null);

  const [data, setData] = useState({
    destinations: [],
    durationPreset: '',
    startDate: '',
    endDate: '',
    adults: 1,
    children: 0,
    babies: 0,
    existingBookings: [],
    tripStyle: '',
    aiGeneration: '',
    withActivities: null,
    familyId: '',
    destinationDays: {},   // {"Paris": 3, "Rome": 4}
    dayBriefs: [],         // [{date, dayNum, label, brief, chips}]
  });

  // ─── Load families if not available ─────────────────────────
  useEffect(() => {
    if (families.length === 0) {
      getFamilies().then(r => setFamilies(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (families.length > 0 && !data.familyId) {
      setData(d => ({ ...d, familyId: families[0].family_id }));
    }
  }, [families]); // eslint-disable-line

  // ─── Auto-distribute destination days ───────────────────────
  useEffect(() => {
    if (data.destinations.length > 1 && data.startDate && data.endDate) {
      const totalDays = Math.max(1, Math.round((new Date(data.endDate) - new Date(data.startDate)) / 86400000) + 1);
      const base = Math.floor(totalDays / data.destinations.length);
      const rem = totalDays % data.destinations.length;
      const newDays = {};
      data.destinations.forEach((dest, i) => {
        newDays[dest] = base + (i === 0 ? rem : 0);
      });
      setData(d => ({ ...d, destinationDays: newDays }));
    }
  }, [data.destinations, data.startDate, data.endDate]); // eslint-disable-line

  // ─── Build day briefs when entering step 7 or when aiGeneration changes ──────────────────
  useEffect(() => {
    if (step === 7 && data.startDate && data.endDate) {
      const days = buildDayList(data.startDate, data.endDate, data.aiGeneration);
      setData(d => ({ ...d, dayBriefs: days }));
    }
  }, [step, data.aiGeneration, data.startDate, data.endDate]); // eslint-disable-line

  // ─── Loading message cycle ───────────────────────────────────
  useEffect(() => {
    if (!creating) return;
    const t = setInterval(() => setLoadingMsg(m => (m + 1) % LOADING_MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, [creating]);

  // ─── AI destination suggest ──────────────────────────────────
  useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (destInput.length < 3) { setAiSuggestions([]); return; }
    suggestTimer.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const res = await aiWizardSuggestDestinations(destInput);
        setAiSuggestions(res.data.suggestions || []);
      } catch { setAiSuggestions([]); }
      setSuggestLoading(false);
    }, 600);
    return () => clearTimeout(suggestTimer.current);
  }, [destInput]);

  const activeSteps = useMemo(() => computeSteps(data), [data.destinations, data.aiGeneration, data.startDate, data.endDate]); // eslint-disable-line
  const stepIndex = activeSteps.findIndex(s => s.id === step);
  const totalSteps = activeSteps.length;

  const go = (targetStep) => {
    setDirection(targetStep > step ? 1 : -1);
    setStep(targetStep);
  };

  const goNext = () => {
    const next = getNextStep(step, data);
    if (next) go(next);
    else handleCreate();
  };

  const goBack = () => {
    const prev = getPrevStep(step, data);
    if (prev) go(prev);
    else navigate(-1);
  };

  const addDestination = (name) => {
    if (!data.destinations.includes(name)) {
      setData(d => ({ ...d, destinations: [...d.destinations, name] }));
    }
    setDestInput('');
    setAiSuggestions([]);
  };

  const applyDuration = (preset) => {
    if (preset.days === null) { setData(d => ({ ...d, durationPreset: preset.id })); return; }
    const start = new Date();
    start.setDate(start.getDate() + 30);
    const end = new Date(start);
    end.setDate(end.getDate() + preset.days - 1);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setData(d => ({ ...d, durationPreset: preset.id, startDate: fmt(start), endDate: fmt(end) }));
  };

  const toggleBooking = (id) => {
    if (id === 'none') { setData(d => ({ ...d, existingBookings: [] })); return; }
    setData(d => ({
      ...d,
      existingBookings: d.existingBookings.includes(id)
        ? d.existingBookings.filter(b => b !== id)
        : [...d.existingBookings, id]
    }));
  };

  const updateDestDays = (dest, delta) => {
    const totalDays = Math.max(1, Math.round((new Date(data.endDate) - new Date(data.startDate)) / 86400000) + 1);
    const current = data.destinationDays[dest] || 0;
    const newVal = Math.max(1, current + delta);
    const otherTotal = Object.entries(data.destinationDays)
      .filter(([k]) => k !== dest)
      .reduce((s, [, v]) => s + v, 0);
    if (newVal + otherTotal > totalDays) return;
    setData(d => ({ ...d, destinationDays: { ...d.destinationDays, [dest]: newVal } }));
  };

  const updateDayBrief = (date, field, value) => {
    setData(d => ({
      ...d,
      dayBriefs: d.dayBriefs.map(b =>
        b.date === date ? { ...b, [field]: value } : b
      )
    }));
  };

  const toggleDayChip = (date, chipId) => {
    setData(d => ({
      ...d,
      dayBriefs: d.dayBriefs.map(b => {
        if (b.date !== date) return b;
        const chips = b.chips.includes(chipId)
          ? b.chips.filter(c => c !== chipId)
          : [...b.chips, chipId];
        return { ...b, chips };
      })
    }));
  };

  const totalDays = data.startDate && data.endDate
    ? Math.max(1, Math.round((new Date(data.endDate) - new Date(data.startDate)) / 86400000) + 1)
    : 0;

  const destDaysTotal = Object.values(data.destinationDays).reduce((s, v) => s + v, 0);

  const canNext = () => {
    if (step === 1) return data.destinations.length > 0;
    if (step === 2) return !!data.startDate && !!data.endDate;
    if (step === 3) return data.adults > 0;
    if (step === 4) return true;
    if (step === 5) return !!data.aiGeneration &&
      (data.aiGeneration === 'none' || data.aiGeneration === 'inspiration' || data.withActivities !== null);
    if (step === 6) return destDaysTotal === totalDays;
    if (step === 7) return true;
    return true;
  };

  const isLastStep = getNextStep(step, data) === null;

  const handleCreate = async () => {
    if (!data.familyId) { toast.error('Sélectionnez une famille'); return; }
    setCreating(true);
    try {
      const payload = {
        destinations: data.destinations,
        start_date: data.startDate,
        end_date: data.endDate,
        adults: data.adults,
        children: data.children,
        babies: data.babies,
        existing_bookings: data.existingBookings,
        trip_style: data.tripStyle,
        ai_generation: data.aiGeneration,
        with_activities: data.withActivities === true,
        family_id: data.familyId,
        currency: 'EUR',
        destination_days: data.destinationDays,
        day_briefs: data.dayBriefs.filter(b => b.brief || b.chips.length > 0),
      };
      const res = await aiWizardCreateTrip(payload);
      toast.success(res.data.ai_generated ? 'Programme généré !' : 'Voyage créé !');
      navigate(`/trips/${res.data.trip_id}`);
    } catch {
      toast.error('Erreur lors de la création');
      setCreating(false);
    }
  };

  // ─── Loading screen ──────────────────────────────────────────
  if (creating) {
    return (
      <div className="fixed inset-0 bg-[#0d0520] flex flex-col items-center justify-center z-50">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center space-y-8 px-6"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="w-20 h-20 rounded-full border-4 border-purple-500/30 border-t-pink-500 mx-auto"
          />
          <div className="space-y-2">
            <p className="text-white/50 text-sm tracking-widest uppercase">LifePulse IA</p>
            <AnimatePresence mode="wait">
              <motion.p
                key={loadingMsg}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-white text-xl font-medium"
              >
                {LOADING_MESSAGES[loadingMsg]}
              </motion.p>
            </AnimatePresence>
          </div>
          {data.destinations.length > 0 && (
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {data.destinations.map((d, i) => (
                <span key={d} className="flex items-center gap-1 text-purple-300 text-sm">
                  {i > 0 && <ArrowRight className="w-3 h-3 opacity-50" />}
                  {d}
                </span>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0d0520] flex flex-col overflow-hidden" data-testid="trip-wizard">

      {/* ─── Top bar ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
        <button onClick={goBack}
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          data-testid="wizard-back-btn">
          <ChevronLeft className="w-4 h-4 text-white/70" />
        </button>

        {/* Dynamic progress dots */}
        <div className="flex items-center gap-2">
          {activeSteps.map((s, i) => (
            <motion.div
              key={s.id}
              animate={{
                width: s.id === step ? 24 : 8,
                opacity: i <= stepIndex ? 1 : 0.25
              }}
              transition={{ duration: 0.3 }}
              className={`h-2 rounded-full cursor-pointer
                ${s.optional ? 'border border-purple-500/50' : ''}
                ${i <= stepIndex ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-white/20'}`}
              onClick={() => i < stepIndex && go(s.id)}
            />
          ))}
        </div>

        <button onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
          <X className="w-4 h-4 text-white/70" />
        </button>
      </div>

      {/* ─── Step content ─────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={step}
            custom={direction}
            variants={slideVariants}
            initial="enter" animate="center" exit="exit"
            className="absolute inset-0 flex flex-col px-5 pb-3 overflow-y-auto"
          >
            {/* ── STEP 1: Destinations ─────────────────────── */}
            {step === 1 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape 1 · Destination</p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Où voulez-vous<br />
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">aller ?</span>
                  </h1>
                  <p className="text-white/40 text-sm">Soyez vague ou précis — Paris, Asie, quelque part chaud...</p>
                </div>

                {data.destinations.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {data.destinations.map((dest, i) => (
                      <motion.div key={dest} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                        className={`flex items-center gap-2 bg-gradient-to-r ${DEST_COLORS[i % DEST_COLORS.length]} rounded-full px-4 py-2 text-white text-sm font-medium`}>
                        <MapPin className="w-3.5 h-3.5" />
                        {dest}
                        <button onClick={() => setData(d => ({ ...d, destinations: d.destinations.filter(x => x !== dest) }))}>
                          <X className="w-3.5 h-3.5 opacity-70 hover:opacity-100" />
                        </button>
                      </motion.div>
                    ))}
                    {data.destinations.length < 5 && (
                      <button onClick={() => document.getElementById('dest-input')?.focus()}
                        className="flex items-center gap-1.5 border border-dashed border-white/20 rounded-full px-4 py-2 text-white/40 text-sm hover:border-white/40 hover:text-white/60 transition-colors">
                        <Plus className="w-3.5 h-3.5" /> Ajouter
                      </button>
                    )}
                  </div>
                )}

                <div className="relative">
                  <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 focus-within:border-purple-500/50 transition-colors">
                    <MapPin className="w-4 h-4 text-white/40 shrink-0" />
                    <input id="dest-input" value={destInput} onChange={e => setDestInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && destInput.trim() && addDestination(destInput.trim())}
                      placeholder="Tapez une destination..." autoFocus
                      className="flex-1 bg-transparent text-white placeholder-white/30 text-base outline-none"
                      data-testid="destination-input" />
                    {destInput.trim() && (
                      <button onClick={() => addDestination(destInput.trim())}
                        className="shrink-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl px-3 py-1.5 text-white text-xs font-medium">
                        Ajouter
                      </button>
                    )}
                  </div>
                  <AnimatePresence>
                    {(aiSuggestions.length > 0 || suggestLoading) && (
                      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                        className="absolute top-full mt-2 w-full bg-[#1a0a38] border border-white/10 rounded-2xl overflow-hidden z-10 shadow-xl shadow-black/40">
                        {suggestLoading ? (
                          <div className="flex items-center gap-3 px-4 py-3 text-white/50 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" /> Recherche...
                          </div>
                        ) : aiSuggestions.map((s, i) => (
                          <button key={i} onClick={() => addDestination(s.name)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left">
                            <MapPin className="w-4 h-4 text-purple-400 shrink-0" />
                            <div>
                              <p className="text-white text-sm font-medium">{s.name}</p>
                              <p className="text-white/40 text-xs">{s.vibe}</p>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="space-y-3">
                  <p className="text-white/40 text-xs uppercase tracking-wider">Destinations populaires</p>
                  <div className="grid grid-cols-4 gap-2">
                    {POPULAR.map(p => (
                      <button key={p.name} onClick={() => addDestination(p.name)}
                        disabled={data.destinations.includes(p.name)}
                        className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl border transition-all
                          ${data.destinations.includes(p.name)
                            ? 'border-purple-500/50 bg-purple-500/20'
                            : 'border-white/8 bg-white/3 hover:border-purple-400/40 hover:bg-white/8'}`}>
                        <span className="text-2xl">{p.emoji}</span>
                        <span className="text-white text-xs font-medium leading-tight">{p.name}</span>
                        {data.destinations.includes(p.name) && (
                          <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-purple-500 flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 2: Dates ────────────────────────────── */}
            {step === 2 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape 2 · Dates</p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Quand
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> partez-vous ?</span>
                  </h1>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  {DURATIONS.map(d => (
                    <button key={d.id} onClick={() => applyDuration(d)}
                      className={`py-3 px-2 rounded-2xl border text-center transition-all
                        ${data.durationPreset === d.id
                          ? 'border-purple-500 bg-purple-500/20 text-white'
                          : 'border-white/10 bg-white/3 text-white/60 hover:border-white/20 hover:text-white/80'}`}
                      data-testid={`duration-${d.id}`}>
                      <p className="text-sm font-semibold">{d.label}</p>
                      {d.days && <p className="text-xs text-white/40">{d.days}j</p>}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[{ label: 'Départ', key: 'startDate' }, { label: 'Retour', key: 'endDate' }].map(({ label, key }) => (
                    <div key={key} className="space-y-2">
                      <label className="text-white/50 text-xs uppercase tracking-wider">{label}</label>
                      <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 focus-within:border-purple-500/50 transition-colors">
                        <input type="date" value={data[key]}
                          onChange={e => setData(d => ({ ...d, [key]: e.target.value, durationPreset: '' }))}
                          className="w-full bg-transparent text-white text-sm outline-none [color-scheme:dark]"
                          data-testid={`wizard-${key}`} />
                      </div>
                    </div>
                  ))}
                </div>
                {data.startDate && data.endDate && (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/20 rounded-2xl p-4 text-center">
                    <p className="text-white font-bold text-2xl">{totalDays} jours</p>
                    <p className="text-white/50 text-sm">{data.startDate} → {data.endDate}</p>
                  </motion.div>
                )}
                {data.destinations.length > 1 && (
                  <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-3 flex gap-2">
                    <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-blue-300 text-sm">Voyage multi-destinations — vous répartirez les jours plus tard.</p>
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 3: Voyageurs ────────────────────────── */}
            {step === 3 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape 3 · Voyageurs</p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Qui part
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> avec vous ?</span>
                  </h1>
                </div>
                <div className="space-y-3">
                  {[
                    { key: 'adults', label: 'Adultes', sub: '18 ans et +', min: 1 },
                    { key: 'children', label: 'Enfants', sub: '3 – 17 ans', min: 0 },
                    { key: 'babies', label: 'Bébés', sub: '0 – 2 ans', min: 0 },
                  ].map(({ key, label, sub, min }) => (
                    <div key={key} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                      <div>
                        <p className="text-white font-medium">{label}</p>
                        <p className="text-white/40 text-sm">{sub}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <button onClick={() => setData(d => ({ ...d, [key]: Math.max(min, d[key] - 1) }))}
                          className="w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center text-white text-xl font-light transition-colors"
                          data-testid={`${key}-minus`}>−</button>
                        <motion.span key={data[key]} initial={{ scale: 1.4, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }}
                          className="text-white text-xl font-bold w-6 text-center tabular-nums" data-testid={`${key}-count`}>
                          {data[key]}
                        </motion.span>
                        <button onClick={() => setData(d => ({ ...d, [key]: d[key] + 1 }))}
                          className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 hover:opacity-90 flex items-center justify-center text-white text-xl font-light transition-opacity"
                          data-testid={`${key}-plus`}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/20 rounded-2xl p-4 text-center">
                  <p className="text-white font-bold text-xl">{data.adults + data.children + data.babies} voyageur{data.adults + data.children + data.babies > 1 ? 's' : ''}</p>
                </div>
              </div>
            )}

            {/* ── STEP 4: Contexte ─────────────────────────── */}
            {step === 4 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape 4 · Contexte</p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Votre
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> situation</span>
                  </h1>
                </div>
                <div className="space-y-3">
                  <p className="text-white/60 text-sm">Avez-vous déjà des réservations ?</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {BOOKING_TYPES.map(({ id, label, Icon }) => (
                      <button key={id} onClick={() => toggleBooking(id)}
                        className={`flex items-center gap-3 p-3.5 rounded-2xl border transition-all
                          ${data.existingBookings.includes(id)
                            ? 'border-purple-500 bg-purple-500/20 text-white'
                            : 'border-white/10 bg-white/3 text-white/60 hover:border-white/20 hover:text-white/80'}`}
                        data-testid={`booking-${id}`}>
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="text-sm font-medium">{label}</span>
                        {data.existingBookings.includes(id) && <Check className="w-3.5 h-3.5 ml-auto" />}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => toggleBooking('none')}
                    className={`w-full py-3 rounded-2xl border text-sm transition-all
                      ${data.existingBookings.length === 0
                        ? 'border-white/30 bg-white/8 text-white'
                        : 'border-white/10 bg-white/3 text-white/50 hover:border-white/20'}`}>
                    Rien encore — je pars de zéro
                  </button>
                </div>
                <div className="space-y-3">
                  <p className="text-white/60 text-sm">Quel type de voyage ? <span className="text-white/30">(optionnel)</span></p>
                  <div className="grid grid-cols-3 gap-2.5">
                    {TRIP_STYLES.map(({ id, label, Icon, color }) => (
                      <button key={id} onClick={() => setData(d => ({ ...d, tripStyle: d.tripStyle === id ? '' : id }))}
                        className={`relative flex flex-col items-center gap-2 p-3.5 rounded-2xl border transition-all overflow-hidden
                          ${data.tripStyle === id ? 'border-transparent text-white' : 'border-white/10 bg-white/3 text-white/60 hover:border-white/20 hover:text-white/80'}`}>
                        {data.tripStyle === id && <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-40`} />}
                        <Icon className="w-5 h-5 relative z-10" />
                        <span className="text-xs font-medium relative z-10">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 5: Programme IA ─────────────────────── */}
            {step === 5 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape 5 · Programme IA</p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Je vous aide à
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> planifier ?</span>
                  </h1>
                </div>
                <div className="space-y-2.5">
                  {AI_OPTIONS.map(({ id, label, sub, Icon, color }) => (
                    <button key={id} onClick={() => setData(d => ({ ...d, aiGeneration: id, withActivities: null }))}
                      className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all text-left
                        ${data.aiGeneration === id ? 'border-transparent bg-white/8' : 'border-white/10 bg-white/3 hover:border-white/20'}`}
                      data-testid={`ai-option-${id}`}>
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold">{label}</p>
                        <p className="text-white/50 text-sm">{sub}</p>
                      </div>
                      {data.aiGeneration === id && (
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <AnimatePresence>
                  {(data.aiGeneration === 'full' || data.aiGeneration === 'partial') && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/30 border border-purple-500/20 rounded-2xl p-4 space-y-3">
                        <p className="text-white font-medium">Avec des activités concrètes ?</p>
                        <div className="grid grid-cols-2 gap-2.5">
                          {[
                            { val: true, label: 'Oui, avec activités', sub: 'Musées, restos, balades...' },
                            { val: false, label: 'Juste la structure', sub: 'Matin / Après-midi / Soir' }
                          ].map(({ val, label, sub }) => (
                            <button key={String(val)} onClick={() => setData(d => ({ ...d, withActivities: val }))}
                              className={`p-3 rounded-xl border text-left transition-all
                                ${data.withActivities === val ? 'border-purple-500 bg-purple-500/20 text-white' : 'border-white/10 bg-white/5 text-white/60 hover:border-white/20'}`}
                              data-testid={`activities-${val}`}>
                              <p className="text-sm font-medium">{label}</p>
                              <p className="text-xs text-white/40 mt-0.5">{sub}</p>
                            </button>
                          ))}
                        </div>
                        {data.destinations.length > 1 && (
                          <p className="text-purple-300 text-xs flex items-center gap-1.5">
                            <Info className="w-3.5 h-3.5" />
                            Vous pourrez répartir les jours par destination à l&apos;étape suivante.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* ── STEP 6: Répartition multi-destinations ────── */}
            {step === 6 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape optionnelle · Répartition</p>
                    <span className="text-xs bg-purple-500/20 text-purple-300 rounded-full px-2 py-0.5 border border-purple-500/30">Optionnel</span>
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Combien de jours
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> par destination ?</span>
                  </h1>
                  <p className="text-white/40 text-sm">Total à distribuer : {totalDays} jours</p>
                </div>

                <div className="space-y-3">
                  {data.destinations.map((dest, i) => {
                    const days = data.destinationDays[dest] || 0;
                    const pct = totalDays > 0 ? (days / totalDays) * 100 : 0;
                    return (
                      <div key={dest} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${DEST_COLORS[i % DEST_COLORS.length]}`} />
                            <p className="text-white font-semibold">{dest}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <button onClick={() => updateDestDays(dest, -1)}
                              className="w-8 h-8 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center text-white transition-colors">−</button>
                            <span className="text-white font-bold text-lg w-8 text-center tabular-nums">{days}</span>
                            <button onClick={() => updateDestDays(dest, 1)}
                              className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 hover:opacity-90 flex items-center justify-center text-white transition-opacity">+</button>
                            <span className="text-white/40 text-sm w-12">jour{days > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                          <motion.div
                            animate={{ width: `${pct}%` }}
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className={`h-full rounded-full bg-gradient-to-r ${DEST_COLORS[i % DEST_COLORS.length]}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total validation */}
                <div className={`rounded-2xl p-4 border text-center transition-all
                  ${destDaysTotal === totalDays
                    ? 'bg-green-900/20 border-green-500/30'
                    : destDaysTotal > totalDays
                    ? 'bg-red-900/20 border-red-500/30'
                    : 'bg-white/5 border-white/10'}`}>
                  <p className={`font-bold text-xl ${destDaysTotal === totalDays ? 'text-green-400' : destDaysTotal > totalDays ? 'text-red-400' : 'text-white'}`}>
                    {destDaysTotal} / {totalDays} jours
                  </p>
                  <p className="text-white/50 text-sm mt-1">
                    {destDaysTotal === totalDays ? 'Parfait !' : destDaysTotal > totalDays ? `Trop de ${destDaysTotal - totalDays} jour(s)` : `Il reste ${totalDays - destDaysTotal} jour(s) à distribuer`}
                  </p>
                </div>
              </div>
            )}

            {/* ── STEP 7: Briefing jour par jour ───────────── */}
            {step === 7 && (
              <div className="max-w-2xl mx-auto w-full pt-5 space-y-7">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-purple-300/80 text-xs font-medium tracking-widest uppercase">Étape optionnelle · Vos journées</p>
                    <span className="text-xs bg-purple-500/20 text-purple-300 rounded-full px-2 py-0.5 border border-purple-500/30">Optionnel</span>
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                    Votre idée
                    <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> pour chaque jour</span>
                  </h1>
                  <p className="text-white/40 text-sm">L&apos;IA s&apos;en inspirera pour construire votre programme. Laissez vide si vous n&apos;avez pas d&apos;idée.</p>
                </div>

                {data.aiGeneration === 'partial' && (
                  <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-3 flex gap-2">
                    <Info className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-blue-300 text-sm">Les 3 premiers jours seront planifiés. Vous pourrez ajouter les suivants ensuite.</p>
                  </div>
                )}

                <div className="space-y-3 pb-4">
                  {data.dayBriefs.map((day, i) => (
                    <motion.div
                      key={day.date}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg px-2.5 py-1 text-white text-xs font-bold shrink-0">
                          J{day.dayNum}
                        </div>
                        <p className="text-white font-medium text-sm">{day.label}</p>
                        {(day.brief || day.chips.length > 0) && (
                          <div className="ml-auto w-2 h-2 rounded-full bg-green-400" />
                        )}
                      </div>

                      {/* Quick chips */}
                      <div className="flex flex-wrap gap-1.5">
                        {DAY_CHIPS.map(({ id, label, Icon }) => (
                          <button key={id} onClick={() => toggleDayChip(day.date, id)}
                            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all
                              ${day.chips.includes(id)
                                ? 'bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white border border-transparent'
                                : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20 hover:text-white/70'}`}
                            data-testid={`chip-${day.date}-${id}`}>
                            <Icon className="w-3 h-3" />
                            {label}
                          </button>
                        ))}
                      </div>

                      {/* Free text */}
                      <input
                        type="text"
                        value={day.brief}
                        onChange={e => updateDayBrief(day.date, 'brief', e.target.value)}
                        placeholder="Votre idée (ex: Musée du Louvre le matin, dîner romantique...)"
                        className="w-full bg-transparent text-white text-sm placeholder-white/25 outline-none border-b border-white/10 focus:border-purple-500/50 pb-1 transition-colors"
                        data-testid={`brief-input-day${day.dayNum}`}
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* ─── Bottom navigation ───────────────────────────────── */}
      <div className="shrink-0 px-5 pb-7 pt-3 space-y-2">
        {/* Summary chip when on last step */}
        {isLastStep && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white/3 border border-white/8 rounded-2xl px-4 py-3 grid grid-cols-3 gap-2 text-center mb-1">
            <div>
              <p className="text-white/40 text-xs">Destination</p>
              <p className="text-white text-sm font-medium truncate">{data.destinations[0] || '–'}{data.destinations.length > 1 ? ` +${data.destinations.length - 1}` : ''}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">Durée</p>
              <p className="text-white text-sm font-medium">{totalDays > 0 ? `${totalDays}j` : '–'}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs">Voyageurs</p>
              <p className="text-white text-sm font-medium">{data.adults + data.children + data.babies}</p>
            </div>
          </motion.div>
        )}

        <button onClick={isLastStep ? handleCreate : goNext}
          disabled={!canNext()}
          className={`w-full h-14 rounded-2xl flex items-center justify-center gap-2 font-semibold text-base transition-all
            ${canNext()
              ? 'bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white shadow-lg shadow-purple-900/50'
              : 'bg-white/5 text-white/20 cursor-not-allowed'}`}
          data-testid={isLastStep ? 'wizard-create-btn' : 'wizard-next-btn'}>
          {isLastStep ? (
            <><Sparkles className="w-5 h-5" />
              {data.aiGeneration && data.aiGeneration !== 'none' ? "Créer avec l'IA" : 'Créer mon voyage'}</>
          ) : (
            <>Continuer <ChevronRight className="w-5 h-5" /></>
          )}
        </button>

        {(step === 4 || step === 6 || step === 7) && (
          <button onClick={isLastStep ? handleCreate : goNext}
            className="w-full text-white/30 text-sm hover:text-white/50 transition-colors py-1">
            {step === 4 ? 'Passer cette étape' : 'Continuer sans renseigner'}
          </button>
        )}
      </div>
    </div>
  );
}
